import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, TextField, CircularProgress } from '@mui/material';
import ProfilePictureUpload from './ProfilePictureUpload';
import { updateLead, updateLeadProfilePicture } from 'apis/leadCaptureApi';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { initializeStorageService, getStorageService } from 'services/storage/storageServiceInitializer';
import PhoneInputField from 'components/common/PhoneInputField';

// Styles for text fields
const textFieldStyles = {
  backgroundColor: '#f7f7f7',
  borderRadius: '12px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
      borderRadius: '12px',
    },
    '&:hover fieldset': {
      border: 'none',
      borderRadius: '12px',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
      borderRadius: '12px',
    },
    '& input': {
      borderRadius: '12px',
      padding: '10px 12px',
    },
    '& input::placeholder': {
      fontSize: '0.8rem',
      fontWeight: '300',
    },
  },
};

// Define contact fields
const CONTACT_FIELDS = [
  { label: 'Name', name: 'name', required: true },
  { label: 'Email', name: 'email', required: true },
  { label: 'Job Title', name: 'jobTitle' },
  { label: 'Company', name: 'company' },
  // Phone number removed from here as it's handled separately
  { label: 'Website', name: 'website' },
  { label: 'Address', name: 'address' },
  { label: 'Instagram', name: 'instagram' },
  { label: 'LinkedIn', name: 'linkedIn' }
];

const ContactDetailsEdit = ({ contact, onCancel, onUpdate }) => {
  // Initialize form data with exact mapping from contact details
  const [formData, setFormData] = useState({
    profilePicture: contact?.profilePictureUrl || null,
    name: contact?.leadName || '',
    email: contact?.leadEmail || '',
    jobTitle: contact?.leadJobTitle || '',
    company: contact?.leadCompany || '',
    phoneNumber: contact?.leadPhoneNumber || '',
    website: contact?.website || '',
    address: contact?.leadAddress || '',
    instagram: contact?.instagram || '',
    linkedIn: contact?.linkedin || '',
    note: contact?.leadNotes || ''
  });

  const [loading, setLoading] = useState(false);
  const [storageService, setStorageService] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  // Initialize storage service
  useEffect(() => {
    const initStorage = async () => {
      await initializeStorageService();
      setStorageService(getStorageService());
    };
    initStorage();
  }, []);

  // Update form data when contact prop changes
  useEffect(() => {
    if (contact) {
      setFormData({
        profilePicture: contact.profilePictureUrl || null,
        name: contact.leadName || '',
        email: contact.leadEmail || '',
        jobTitle: contact.leadJobTitle || '',
        company: contact.leadCompany || '',
        phoneNumber: contact.leadPhoneNumber || '',
        website: contact.website || '',
        address: contact.leadAddress || '',
        instagram: contact.instagram || '',
        linkedIn: contact.linkedin || '',
        note: contact.leadNotes || ''
      });
    }
  }, [contact]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle phone number changes
  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: value
    }));
    setPhoneError('');
  };

  // Update profile picture
  const handleProfilePictureUpdate = (pictureBlob) => {
    setFormData(prev => ({
      ...prev,
      profilePicture: pictureBlob
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const requiredFields = ['name', 'email'];
    const isValid = requiredFields.every(field => formData[field]?.trim());

    if (isValid) {
      try {
        // Transform formData to match the backend structure
        const updateData = {
          leadName: formData.name,
          leadEmail: formData.email,
          leadJobTitle: formData.jobTitle,
          leadCompany: formData.company,
          leadPhoneNumber: formData.phoneNumber,
          website: formData.website,
          leadAddress: formData.address,
          instagram: formData.instagram,
          linkedin: formData.linkedIn,
          leadNotes: formData.note,
          // Preserve other fields from the original contact
          userId: contact.userId,
          cardId: contact.cardId,
          customFields: contact.customFields,
          leadSource: contact.leadSource
        };

        // Call the API to update the lead
        const response = await updateLead(contact._id, updateData);
        let updatedContact = response.lead;

        // If there's a profile picture (as a Blob), upload it
        if (formData.profilePicture instanceof Blob) {
          try {
            // Upload the profile picture with the new structure
            const profilePictureUrl = await uploadImageBlob(
              formData.profilePicture,
              null, // No state setter needed
              'lead',
              'profile',
              contact._id
            );

            // Update the lead with the new profile picture URL
            const pictureResponse = await updateLeadProfilePicture(contact._id, profilePictureUrl);
            updatedContact = pictureResponse.lead; // Use the updated lead response
          } catch (imageError) {
            // Continue with the update even if the image upload fails
          }
        } else if (formData.profilePicture === null && contact.profilePictureUrl) {
          // Handle profile picture removal
          try {
            // Update the lead with null profile picture URL
            const pictureResponse = await updateLeadProfilePicture(contact._id, null);
            updatedContact = pictureResponse.lead;

            // Delete the image using the new structure
            await deleteImage('lead', 'profile', contact._id);
          } catch (profileError) {
            // Profile picture removal failed
          }
        }

        // Call the onUpdate callback with the updated lead
        if (onUpdate) {
          onUpdate(updatedContact);
        }
      } catch (error) {
        alert('Failed to update contact. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
      alert('Please fill in required fields (Name and Email)');
    }
  };

  return (
    <Box sx={{ mt: 3, overflow: 'hidden' }}>
      <form onSubmit={handleSubmit}>
        <ProfilePictureUpload
          initialProfilePicture={contact?.profilePictureUrl}
          onProfilePictureUpdate={handleProfilePictureUpdate}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {CONTACT_FIELDS.map((field) => (
            <Box key={field.name}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#4f4f4f',
                  marginBottom: '10px'
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </Typography>
              <TextField
                fullWidth
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleInputChange}
                placeholder={field.label}
                required={field.required}
                sx={{
                  ...textFieldStyles,
                  '& .MuiInputBase-root': {
                    height: '48px'
                  }
                }}
              />
            </Box>
          ))}

          <Box>
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '10px'
              }}
            >
              Phone Number
            </Typography>
            <PhoneInputField
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              error={phoneError}
              placeholder="Enter phone number"
              containerStyle={{ marginBottom: '10px' }}
            />
          </Box>

          <Box sx={{ gridColumn: '1 / span 2', marginTop: 2 }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '10px'
              }}
            >
              Note
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              name="note"
              value={formData.note || ''}
              onChange={handleInputChange}
              placeholder="Note"
              sx={{
                ...textFieldStyles,
                '& .MuiInputBase-root': {
                  height: 'auto',
                  minHeight: '90px'
                }
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            marginTop: 3,
            paddingTop: 2,
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            sx={{
              backgroundColor: 'white',
              color: '#4f4f4f',
              borderRadius: '20px',
              border: '1px solid #e3e3e3',
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: '600',
              '&:hover': {
                backgroundColor: 'white',
                boxShadow: 2,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              borderRadius: '20px',
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: '600',
              '&:hover': {
                backgroundColor: '#000',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ContactDetailsEdit;